import { useSuspenseQuery } from '@tanstack/react-query';
import type { FacebookPage } from '../types';
import queryKeys from './queryKeys';
import type { QueryConfig } from 'lib/react-query';
import { apiClient } from 'lib/api';
import { isAxiosError } from 'axios';

interface GetParams {
  id?: string;
}

export const getFacebookPageDetails = async (
  { id, ...params }: GetParams,
  signal?: AbortSignal
): Promise<FacebookPage> => {
  const res = await apiClient.get<FacebookPage>(
    `${queryKeys.facebookPages}/${id}`,
    {
      signal,
      params,
    }
  );

  if (isAxiosError(res)) {
    const error = new Error(
      `Get facebook page details. Status: ${res.response?.status}`
    );
    error.cause = {
      ...res.response?.data,
      status: res.response?.status,
    };
    return Promise.reject(error);
  }

  return res.data;
};

interface UseOptions<TData> {
  config?: QueryConfig<typeof getFacebookPageDetails, TData>;
  params: GetParams;
}

export const useFacebookPageDetails = <TData = FacebookPage>({
  config,
  params,
}: UseOptions<TData>) => {
  return useSuspenseQuery({
    staleTime: 60 * 30 * 1000,
    ...config,
    queryFn: ({ signal }) => getFacebookPageDetails(params, signal),
    queryKey: [queryKeys.facebookPageDetails, params.id],
  });
};
